import React from "react";
import { useTranslation } from "react-i18next";
import {
  Drawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  Button,
  Text,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  DismissRegular,
  ErrorCircleRegular,
  WarningRegular,
  ChevronRightRegular,
} from "@fluentui/react-icons";

const useStyles = makeStyles({
  drawer: {
    zIndex: 1000,
  },
  errorItem: {
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: "pointer",
    backgroundColor: "transparent",
    border: "none",
    width: "100%",
    textAlign: "left",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
  },
  warningItem: {
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: "pointer",
    backgroundColor: "transparent",
    border: "none",
    width: "100%",
    textAlign: "left",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
  },
  errorIcon: {
    color: tokens.colorPaletteRedForeground1,
    marginRight: "12px",
    flexShrink: 0,
  },
  warningIcon: {
    color: tokens.colorPaletteYellowForeground2,
    marginRight: "12px",
    flexShrink: 0,
  },
  errorContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  errorField: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  errorMessage: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground2,
    lineHeight: tokens.lineHeightBase100,
  },
  errorPath: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    fontStyle: "italic",
  },
  chevronIcon: {
    color: tokens.colorNeutralForeground3,
    marginLeft: "8px",
    flexShrink: 0,
  },
  header: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px",
    paddingRight: "8px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  headerTitle: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    paddingRight: "48px",
  },
  headerIcon: {
    color: tokens.colorPaletteRedForeground1,
  },
  noErrors: {
    textAlign: "center",
    padding: "32px 16px",
    color: tokens.colorNeutralForeground2,
  },
  errorCount: {
    color: tokens.colorPaletteRedForeground1,
    fontWeight: tokens.fontWeightSemibold,
    marginLeft: "4px",
  },
  closeButton: {
    position: "absolute",
    top: "8px",
    right: "8px",
    minWidth: "32px",
    minHeight: "32px",
    padding: "6px",
  },
  sectionTitle: {
    padding: "16px",
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    display: "block",
    fontWeight: tokens.fontWeightSemibold,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  instructionText: {
    padding: "16px",
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    display: "block",
  },
});

export interface ValidationError {
  field: string;
  fieldPath: string;
  message: string;
  tabName?: string;
  sectionPath?: string;
}

export interface CalculationMessage {
  level: "warning" | "error";
  message: string;
  code?: string;
}

interface ValidationErrorSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  errors: ValidationError[];
  calculationMessages?: CalculationMessage[];
  onErrorClick: (error: ValidationError) => void;
  onCalculationMessageClick?: (message: CalculationMessage) => void;
}

const ValidationErrorSidebar: React.FC<ValidationErrorSidebarProps> = ({
  isOpen,
  onClose,
  errors,
  calculationMessages = [],
  onErrorClick,
  onCalculationMessageClick,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();

  // Group calculation messages by level
  const calculationErrors = calculationMessages.filter(
    (msg) => msg.level === "error"
  );
  const calculationWarnings = calculationMessages.filter(
    (msg) => msg.level === "warning"
  );

  const totalCount =
    errors.length + calculationErrors.length + calculationWarnings.length;
  const hasAnyMessages = totalCount > 0;

  return (
    <Drawer
      type="overlay"
      separator
      open={isOpen}
      onOpenChange={(_, { open }) => !open && onClose()}
      position="end"
      size="medium"
      className={styles.drawer}
    >
      <DrawerHeader className={styles.header}>
        <div className={styles.headerTitle}>
          <ErrorCircleRegular className={styles.headerIcon} />
          <DrawerHeaderTitle>
            {t("qtg.validation.errorsTitle")}
            {totalCount > 0 && (
              <span className={styles.errorCount}>({totalCount})</span>
            )}
          </DrawerHeaderTitle>
        </div>
        <Button
          appearance="subtle"
          aria-label={t("qtg.validation.close")}
          icon={<DismissRegular />}
          onClick={onClose}
          className={styles.closeButton}
        />
      </DrawerHeader>
      <DrawerBody>
        {!hasAnyMessages ? (
          <div className={styles.noErrors}>
            <Text>{t("qtg.validation.noErrors")}</Text>
          </div>
        ) : (
          <>
            {/* Calculation errors section */}
            {calculationErrors.length > 0 && (
              <>
                <Text className={styles.sectionTitle}>
                  {t("qtg.validation.calculationErrors")}
                </Text>
                {calculationErrors.map((message, index) => (
                  <button
                    key={`calc-error-${index}`}
                    className={styles.errorItem}
                    onClick={() => onCalculationMessageClick?.(message)}
                  >
                    <ErrorCircleRegular className={styles.errorIcon} />
                    <div className={styles.errorContent}>
                      <div className={styles.errorMessage}>
                        {message.message}
                      </div>
                      {message.code && (
                        <div className={styles.errorPath}>{message.code}</div>
                      )}
                    </div>
                    <ChevronRightRegular className={styles.chevronIcon} />
                  </button>
                ))}
              </>
            )}

            {/* Calculation warnings section */}
            {calculationWarnings.length > 0 && (
              <>
                <Text className={styles.sectionTitle}>
                  {t("qtg.validation.calculationWarnings")}
                </Text>
                {calculationWarnings.map((message, index) => (
                  <button
                    key={`calc-warning-${index}`}
                    className={styles.warningItem}
                    onClick={() => onCalculationMessageClick?.(message)}
                  >
                    <WarningRegular className={styles.warningIcon} />
                    <div className={styles.errorContent}>
                      <div className={styles.errorMessage}>
                        {message.message}
                      </div>
                      {message.code && (
                        <div className={styles.errorPath}>{message.code}</div>
                      )}
                    </div>
                    <ChevronRightRegular className={styles.chevronIcon} />
                  </button>
                ))}
              </>
            )}

            {/* Form validation errors section */}
            {errors.length > 0 && (
              <>
                <Text className={styles.sectionTitle}>
                  {t("qtg.validation.formErrors")}
                </Text>
                <Text className={styles.instructionText}>
                  {t("qtg.validation.clickToNavigate")}
                </Text>
                {errors.map((error, index) => (
                  <button
                    key={`${error.fieldPath}-${index}`}
                    className={styles.errorItem}
                    onClick={() => onErrorClick(error)}
                  >
                    <ErrorCircleRegular className={styles.errorIcon} />
                    <div className={styles.errorContent}>
                      <div className={styles.errorField}>
                        {t(error.field, error.field)}
                      </div>
                      <div className={styles.errorMessage}>
                        {t(error.message, error.message)}
                      </div>
                      {error.tabName && (
                        <div className={styles.errorPath}>
                          {t(`qtg.tabs.${error.tabName}`, error.tabName)}
                          {error.sectionPath && ` › ${error.sectionPath}`}
                        </div>
                      )}
                    </div>
                    <ChevronRightRegular className={styles.chevronIcon} />
                  </button>
                ))}
              </>
            )}
          </>
        )}
      </DrawerBody>
    </Drawer>
  );
};

export default ValidationErrorSidebar;
